import { useCallback, useEffect, useRef, useState } from "react";
import { PatientNotificationPreference } from "../../services";
import { useUpdateNotificationPreferences } from "./use-update-notification-preferences";

type Channel = "push" | "text" | "email";

interface UseSaveNotificationPreferencesProps {
  patientUuid: string;
  onSaveWhileUnMount?: (changedPreferences: PatientNotificationPreference[]) => void
}

export const useSaveNotificationPreferences = ({
  patientUuid,
  onSaveWhileUnMount
}: UseSaveNotificationPreferencesProps) => {
  const [preferences, setPreferences] = useState<
    PatientNotificationPreference[]
  >([]);
  const changedIdsRef = useRef<Set<number>>(new Set());
  const preferencesRef = useRef<PatientNotificationPreference[]>([]);
  const patientUuidRef = useRef(patientUuid);

  const updateMutation = useUpdateNotificationPreferences();
  const mutateRef = useRef(updateMutation.mutate);
  mutateRef.current = updateMutation.mutate;

  const onSaveWhileUnMountRef = useRef(onSaveWhileUnMount);
  onSaveWhileUnMountRef.current = onSaveWhileUnMount;

  useEffect(() => {
    patientUuidRef.current = patientUuid;
  }, [patientUuid]);

  const saveChanges = useCallback(() => {
    if (changedIdsRef.current.size === 0 || !patientUuidRef.current) return;
    const changedPreferences = preferencesRef.current.filter((pref) =>
      changedIdsRef.current.has(pref.notificationTypeId!)
    );
    if (changedPreferences.length > 0) {
      if (onSaveWhileUnMountRef.current) {
        onSaveWhileUnMountRef.current(changedPreferences);
      } else {
        mutateRef.current({
          patientUuid: patientUuidRef.current,
          preferences: changedPreferences,
        });
      }
      changedIdsRef.current.clear();
    }
  }, []);

  // Save on unmount
  useEffect(() => {
    return () => {
      saveChanges();
    };
  }, [saveChanges]);

  const setList = useCallback((items: PatientNotificationPreference[]) => {
    setPreferences(items);
    preferencesRef.current = items;
    changedIdsRef.current.clear();
  }, []);

  const onChangePreference = useCallback(
    (notificationTypeId: number, channel: Channel) => {
      setPreferences((prev) => {
        const updated = prev.map((pref) =>
          pref.notificationTypeId === notificationTypeId
            ? { ...pref, [channel]: !pref[channel] }
            : pref
        );
        preferencesRef.current = updated;
        return updated;
      });
      changedIdsRef.current.add(notificationTypeId);
    },
    []
  );

  return { preferences, setList, onChangePreference, saveChanges };
};
